import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const faqs = [
  {
    question: 'How does prepxai simulate real interview pressure?',
    answer: 'prepxai utilizes dynamic voice interaction, adaptive coding panels, and system design whiteboards that react to your responses. The AI interviewer can follow up on weak assertions, interrupt when you over-explain, and adjust the difficulty in real-time to mimic actual interviewer behaviors.',
  },
  {
    question: 'Can I practice for specific companies like Google or Meta?',
    answer: 'Yes, you can configure your practice rounds to target specific company cultures and rubric standards. The AI is primed with real interview feedback data and grading guidelines from top-tier tech firms to focus on the exact competencies they grade.',
  },
  {
    question: 'What roles and seniority levels are supported?',
    answer: 'We support a wide range of roles including Software Engineer (Backend, Frontend, Full Stack), Systems Engineer, Data Engineer, Product Manager, Data Analyst, and DevOps. You can choose any level from Junior Intern to Principal Engineer.',
  },
  {
    question: 'How detailed is the scorecard feedback?',
    answer: 'Each scorecard provides a detailed breakdown of your structure, technical depth, pacing, and filler word usage. You get direct comparisons between your responses and recommended model answers, pointing out exactly what recruiters check.',
  },
  {
    question: 'Is there a free trial available?',
    answer: 'Every new account receives free starting coins to practice full interview rounds immediately. No credit card is required to sign up and try out our AI interviewer.',
  },
  {
    question: 'Can I review my past mock interview history?',
    answer: 'Yes, your dashboard preserves a complete chronological timeline of every scorecard, audio transcript, and code submission. This lets you visualize your score progression and track improvements in real-time.',
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleIndex = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section id="faq" className="relative border-t border-[var(--lp-line)] bg-[var(--lp-bg)] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="font-mono text-[11px] font-semiboldtracking-[0.2em] text-[var(--lp-accent)]">
            FAQ
          </p>
          <h2 className="mt-3 font-semibold text-4xl leading-tight text-[var(--lp-ink)] sm:text-5xl">
            Frequently Asked Questions
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-[var(--lp-ink-soft)]">
            Everything you need to know about the prepxai platform and interview prep rounds.
          </p>
        </motion.div>

        <div className="mx-auto mt-16 max-w-3xl divide-y divide-[var(--lp-line)]">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={index} className="py-2 first:pt-0 last:pb-0">
                <button
                  type="button"
                  onClick={() => toggleIndex(index)}
                  className="flex w-full items-center justify-between py-5 text-left transition-colors focus:outline-none"
                  aria-expanded={isOpen}
                >
                  <span className="text-base font-medium leading-7 text-[var(--lp-ink)] pr-4">
                    {faq.question}
                  </span>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[var(--lp-line)] text-[var(--lp-ink-soft)] shrink-0 transition-transform duration-200">
                    <Icon
                      name={isOpen ? 'Minus' : 'Plus'}
                      size={15}
                      className="text-[var(--lp-ink)]"
                    />
                  </span>
                </button>

                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{
                    height: isOpen ? 'auto' : 0,
                    opacity: isOpen ? 1 : 0,
                  }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <p className="pb-5 text-sm leading-7 text-[var(--lp-ink-soft)]">
                    {faq.answer}
                  </p>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
